import Alpine from 'alpinejs';

// Maximum tilt amount in degrees. Higher = more dramatic.
const MAX_TILT_DEG = 15;

// Maximum roll (2D rotate around the screen's normal) in degrees,
// driven by the cursor's horizontal position. -MAX at the far left,
// +MAX at the far right, 0° at centre. Lerp/settle uses the same
// constants as the tilt so the roll feels weighted to match.
const MAX_ROLL_DEG = 6;

// Lerp factor: how much of the gap to target we close each frame.
// 0.06–0.10 feels cinematic and weighty (vanilla-tilt / smooth-scroll
// territory). Higher = snappier, lower = draggier.
const LERP_FACTOR = 0.08;

// When current is within this many degrees of target, we snap and
// stop the rAF loop to save cycles.
const SETTLE_EPSILON = 0.02;

Alpine.data('interactiveGraphic', () => ({
  graphic: null,
  rafId: null,
  active: false,
  cleanups: [],

  // Where we want to be (set instantly on mousemove)
  targetX: 0,
  targetY: 0,
  targetZ: 0,

  // Where we currently are (eased toward target each frame)
  currentX: 0,
  currentY: 0,
  currentZ: 0,

  init() {
    this.$nextTick(() => this.setup());
  },

  setup() {
    this.graphic = this.$root.querySelector('.interactiveGraphic_graphic');
    if (!this.graphic) return;

    const onMove = (e) => {
      const rect = this.$root.getBoundingClientRect();
      const normX = (e.clientX - rect.left) / rect.width;
      const normY = (e.clientY - rect.top) / rect.height;

      // Cursor lifts the nearest corner toward the viewer.
      this.targetX = (normY - 0.5) * 2 * MAX_TILT_DEG;
      this.targetY = (0.5 - normX) * 2 * MAX_TILT_DEG;

      // Z roll — proportional to horizontal cursor offset from
      // centre. normX = 0   → -MAX_ROLL_DEG (far left)
      //         normX = 0.5 →  0°            (centre)
      //         normX = 1   → +MAX_ROLL_DEG (far right)
      // Halfway between centre and an edge gives half the roll
      // (≈ 2.5° at the defaults), matching the brief.
      this.targetZ = (normX - 0.5) * 2 * MAX_ROLL_DEG;

      this.start();
    };

    const onLeave = () => {
      // Settle flat when the cursor leaves.
      this.targetX = 0;
      this.targetY = 0;
      this.targetZ = 0;
      this.start();
    };

    this.$root.addEventListener('mousemove', onMove);
    this.$root.addEventListener('mouseleave', onLeave);

    this.cleanups.push(() => this.$root.removeEventListener('mousemove', onMove));
    this.cleanups.push(() => this.$root.removeEventListener('mouseleave', onLeave));
  },

  start() {
    if (this.active) return;
    this.active = true;
    this.tick();
  },

  tick() {
    // Linear interpolate current toward target. This is what gives
    // the motion its weight: as targets jump around per mousemove,
    // current glides smoothly toward them frame by frame.
    this.currentX += (this.targetX - this.currentX) * LERP_FACTOR;
    this.currentY += (this.targetY - this.currentY) * LERP_FACTOR;
    this.currentZ += (this.targetZ - this.currentZ) * LERP_FACTOR;

    this.graphic.style.setProperty('--rotate-x', this.currentX.toFixed(3));
    this.graphic.style.setProperty('--rotate-y', this.currentY.toFixed(3));
    this.graphic.style.setProperty('--rotate-z', this.currentZ.toFixed(3));

    const dx = Math.abs(this.targetX - this.currentX);
    const dy = Math.abs(this.targetY - this.currentY);
    const dz = Math.abs(this.targetZ - this.currentZ);

    if (dx < SETTLE_EPSILON && dy < SETTLE_EPSILON && dz < SETTLE_EPSILON) {
      // Snap to exact targets and pause the loop until next move.
      this.currentX = this.targetX;
      this.currentY = this.targetY;
      this.currentZ = this.targetZ;
      this.graphic.style.setProperty('--rotate-x', this.targetX.toFixed(3));
      this.graphic.style.setProperty('--rotate-y', this.targetY.toFixed(3));
      this.graphic.style.setProperty('--rotate-z', this.targetZ.toFixed(3));
      this.active = false;
      this.rafId = null;
      return;
    }

    this.rafId = requestAnimationFrame(() => this.tick());
  },

  destroy() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.active = false;
    this.cleanups.forEach(fn => fn());
    this.cleanups = [];
  }
}));
